import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import {
  ApiExternalOutgoingFiatNetworkQuery,
  ApiExternalOutgoingFiatNetworkResponse,
  ApiExternalOutgoingProviderQuery,
  ApiExternalOutgoingProviderResponse,
  ApiExternalOutgoingTransferCreateRequest,
  ApiExternalOutgoingTransferIntentRequest,
  ApiExternalOutgoingTransferIntentResponse,
  ApiExternalTransferResponse,
  ApiInternalTransferCreateRequest,
  ApiInternalTransferIntentRequest,
  ApiInternalTransferIntentResponse,
  ApiMoney,
  ApiOwnTransferCreateRequest,
  ApiOwnTransferIntentRequest,
  ApiOwnTransferIntentResponse,
} from '../openapi';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { mapResponse } from './helpers';
import { isCryptoCurrency } from '@razz-team/utils';
import { useMemo } from 'react';

export function useOwnTransferIntentQuery(request: ApiOwnTransferIntentRequest | undefined) {
  const client = useApiClient();
  return useQuery<ApiOwnTransferIntentResponse>({
    queryKey: ['transfers', 'own', 'intent', request],
    queryFn: ({ signal }) =>
      client
        .POST('/transfers/own/intent', {
          signal,
          body: request!,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    enabled: Boolean(request),
    retry: 1,
    retryDelay: 500,
    refetchInterval: 5000,
  });
}

function getCounterCurrency(currency: string) {
  if (currency === 'EUR') return 'USDC';
  if (currency === 'USDC') return 'EUR';
  if (isCryptoCurrency(currency)) return 'USDC';
  return 'EUR';
}

export function useBalanceRate(amount: ApiMoney | null | undefined) {
  const request = useMemo(() => {
    if (!amount) return undefined;
    return {
      amount,
      sourceCurrency: amount.currency,
      targetCurrency: getCounterCurrency(amount.currency),
      side: 'SELL',
    } satisfies ApiOwnTransferIntentRequest;
  }, [amount]);
  const intentQuery = useOwnTransferIntentQuery(request);
  return intentQuery.isSuccess
    ? {
        counterAmount: intentQuery.data.quote.targetAmount,
        rate: {
          amount: intentQuery.data.quote.fx?.rate.bid ?? 1, // if no rate, assume 1:1,
          currency: intentQuery.data.quote.targetAmount.currency,
        },
      }
    : null;
}

export function useCreateOwnTransferMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['transfers', 'own', 'create', idempotencyKey],
    mutationFn: (body: ApiOwnTransferCreateRequest) =>
      client
        .POST('/transfers/own', {
          body,
          params: { path: { id: 'create' } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useTransfersExternalIncomingNetworksCryptoQuery({
  currency,
}: {
  currency: string | undefined;
}) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['transfers', 'external', 'incoming', 'networks', 'crypto', currency],
    queryFn: ({ signal }) =>
      client
        .GET('/transfers/external/incoming/networks/crypto', {
          params: { query: { currency }, signal },
        })
        .then(mapResponse),
    enabled: Boolean(currency),
  });
}

export function useOutgoingExternalTransferIntentQuery(
  request: ApiExternalOutgoingTransferIntentRequest | undefined,
) {
  const client = useApiClient();
  return useQuery<ApiExternalOutgoingTransferIntentResponse>({
    queryKey: ['transfers', 'external', 'outgoing', 'intent', request],
    queryFn: ({ signal }) =>
      client
        .POST('/transfers/external/outgoing/intent', {
          signal,
          body: request!,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    enabled: Boolean(request),
    retry: 1,
    retryDelay: 500,
  });
}

export function useCreateOutgoingExternalTransferMutation({
  idempotencyKey,
}: {
  idempotencyKey: string;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['transfers', 'external', 'outgoing', idempotencyKey],
    mutationFn: (body: ApiExternalOutgoingTransferCreateRequest) =>
      client
        .POST('/transfers/external/outgoing', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: (data) => {
      if (data?.type === 'CHALLENGES_COMPLETED') {
        queryClient.invalidateQueries();
      }
    },
  });
}

export function useInternalTransferIntentQuery(
  request: ApiInternalTransferIntentRequest | undefined,
) {
  const client = useApiClient();
  return useQuery<ApiInternalTransferIntentResponse>({
    queryKey: ['transfers', 'internal', 'intent', request],
    queryFn: ({ signal }) =>
      client
        .POST('/transfers/internal/outgoing/intent', {
          signal,
          body: request as unknown as ApiInternalTransferIntentRequest,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    enabled: Boolean(request),
    retry: 1,
    retryDelay: 500,
  });
}

export function useCreateInternalTransferMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['transfers', 'internal', idempotencyKey],
    mutationFn: (body: ApiInternalTransferCreateRequest) =>
      client
        .POST('/transfers/internal/outgoing', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then((res) => res.data as unknown as ApiExternalTransferResponse),
    onSuccess: (data) => {
      if (data?.type === 'CHALLENGES_COMPLETED') {
        queryClient.invalidateQueries();
      }
    },
  });
}

export function useExternalOutgoingCryptoNetworks({
  currency,
}: {
  currency: string | null | undefined;
}) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['transfers', 'external', 'outgoing', 'crypto', 'networks', currency],
    queryFn: ({ signal }) =>
      client
        .GET('/transfers/external/outgoing/networks/crypto', {
          signal,
          params: { query: { currency: currency! } },
        })
        .then(mapResponse),
    enabled: Boolean(currency),
  });
}

export function useExternalOutgoingFiatNetwork(
  query: ApiExternalOutgoingFiatNetworkQuery | undefined,
) {
  const client = useApiClient();
  return useQuery<ApiExternalOutgoingFiatNetworkResponse>({
    queryKey: ['transfers', 'external', 'outgoing', 'fiat', 'networks', query],
    queryFn: ({ signal }) =>
      client
        .GET('/transfers/external/outgoing/network', {
          signal,
          params: { query: query! },
        })
        .then(mapResponse),
    enabled: Boolean(query),
    placeholderData: keepPreviousData,
  });
}

export function useExternalOutgoingProviderQuery(
  query: ApiExternalOutgoingProviderQuery | undefined,
) {
  const client = useApiClient();
  return useQuery<ApiExternalOutgoingProviderResponse>({
    queryKey: ['transfers', 'external', 'outgoing', 'provider', query],
    queryFn: ({ signal }) =>
      client
        .GET('/transfers/external/outgoing/provider', {
          signal,
          params: { query: query! },
        })
        .then(mapResponse),
    enabled: Boolean(query),
    placeholderData: keepPreviousData,
  });
}

export function usePopCodesQuery({
  country,
  currency,
  provider,
}: {
  country: string | null | undefined;
  provider: string | null | undefined;
  currency: string | null | undefined;
}) {
  return useQuery({
    queryKey: ['pop-codes', country, currency, provider],
    queryFn: async (): Promise<{ default: Record<string, string> }> => {
      if (country === 'AE') {
        return import('./pop-codes/ae-pop-codes.json');
      }
      if (currency === 'GBP' && provider === 'BREINROCK') {
        return import('./pop-codes/gb-pop-codes.json');
      }

      return { default: {} };
    },
    select: (data) => data?.default,
    enabled: Boolean(country && currency),
  });
}

export function useDeleteTransferMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['payouts', 'transfers', 'delete'],
    mutationFn: ({
      transferId,
      createdAt,
      type,
    }: {
      transferId: string;
      createdAt: string;
      type: 'internal' | 'external';
    }) =>
      type === 'internal'
        ? client
            .DELETE('/transfers/internal/outgoing/{id}', {
              params: { path: { id: transferId }, query: { createdAt } },
            })
            .then(mapResponse)
        : client
            .DELETE('/transfers/external/outgoing/{id}', {
              params: { path: { id: transferId }, query: { createdAt } },
            })
            .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function useUploadEvidenceMutation() {
  const client = useApiClient();
  return useMutation({
    mutationKey: ['transfers', 'evidence'],
    mutationFn: (body: FormData): Promise<{ documents: Record<string, string> }> =>
      client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .POST('/transfers/evidence' as any, {
          body,
          headers: { 'Idempotency-Key': createIdempotencyKey() },
          bodySerializer: () => body,
        })
        .then(mapResponse),
    retry: true,
  });
}
